<template>
  <div class="standalone-auth">
    <div class="standalone-auth__card standalone-auth__card--wide">
      <LogoTile :branding="branding" :alt="tenantName" :size="40" />
      <h1 class="standalone-auth__title">Choose an organisation</h1>
      <v-list class="standalone-orgpicker__list" density="comfortable">
        <v-list-item
          v-for="org in session.accessibleOrgs"
          :key="org.id"
          :disabled="selecting"
          @click="choose(org)"
        >
          <template #prepend>
            <LogoTile :src="org.logo || org.icon || null" :alt="org.name" :size="28" />
          </template>
          <v-list-item-title>{{ org.name }}</v-list-item-title>
        </v-list-item>
      </v-list>
      <p v-if="error" class="standalone-auth__error">{{ error }}</p>
      <v-btn variant="text" size="small" @click="session.signOut()">Sign out</v-btn>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { useSessionStore } from "@/stores/session";
import LogoTile from "@/shell/LogoTile.vue";
import type { UserOrganisation } from "@/tenant/types";

export default defineComponent({
  name: "OrgPicker",
  components: { LogoTile },
  setup() {
    const session = useSessionStore();
    const selecting = ref(false);
    const error = ref<string | null>(null);

    const tenantName = computed(() => session.tenant?.name || "MetaShip");
    const branding = computed(() => session.tenant?.branding ?? null);

    async function choose(org: UserOrganisation): Promise<void> {
      selecting.value = true;
      error.value = null;
      try {
        await session.selectOrganisation(org);
      } catch (e) {
        error.value = e instanceof Error ? e.message : "Could not switch organisation — please try again.";
      } finally {
        selecting.value = false;
      }
    }

    return { session, tenantName, branding, selecting, error, choose };
  },
});
</script>

<style scoped>
.standalone-auth {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.standalone-auth__card {
  width: 100%;
  max-width: 340px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  text-align: center;
}
.standalone-auth__card--wide {
  max-width: 400px;
}
.standalone-auth__title {
  font-size: 18px;
  font-weight: 700;
  margin: 0;
}
.standalone-orgpicker__list {
  width: 100%;
  max-height: 320px;
  overflow-y: auto;
}
.standalone-auth__error {
  color: rgb(var(--v-theme-error));
  font-size: 13px;
  margin: 0;
}
</style>
